import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  PublicQuoteView,
  Quote,
  QuoteApprovalPlacement,
  QuoteLinesFrequency,
  QuoteTemplate,
  UpdateQuoteInput,
} from '@catalyst-psa/shared';
import {
  QUOTE_LEGAL_SECTION_INDEX,
  QUOTE_LEGAL_SECTION_TITLE,
  QUOTE_LINES_FREQUENCY_LABELS,
  QUOTE_STATUS_LABELS,
} from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { useDetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { Modal } from '../../components/ui/Modal.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { SelectField, CheckboxField } from '../../components/ui/Field.js';
import {
  collectNoteImageIds,
  hydrateNoteImages,
  serializeNoteImages,
} from '../../components/ui/RichText.js';
import { ApiError, mutationError } from '../../api/client.js';
import { formatDate, formatDateTime } from '../../utils/date.js';
import {
  resolveOpportunityImages,
  uploadOpportunityImage,
} from '../opportunities/api.js';
import {
  useApplyQuoteTemplate,
  useCreateQuote,
  useDeleteQuote,
  useDisableQuoteSowLink,
  usePreviewQuotePdf,
  usePromoteQuote,
  useQuote,
  useQuoteDeliveryPreview,
  useQuoteTemplate,
  useQuoteTemplates,
  useQuoteVisits,
  useQuotes,
  useRegenerateQuoteSowLink,
  useUpdateQuote,
} from './api.js';
import { QuoteDeliveryView } from './QuoteDeliveryView.js';
import {
  QuoteLinesEditor,
  defaultLineLabels,
  toLineForms,
  toLineInputs,
  toLineLabelInputs,
  toLineLabelsForm,
  type LineForm,
  type LineLabelsForm,
} from './QuoteLinesEditor.js';
import { SendQuoteEmailModal } from './SendQuoteEmailModal.js';
import { QUOTE_STATUS_TONES } from './status.js';
import {
  QuoteSectionsEditor,
  toSectionForms,
  toSectionInputs,
  type SectionForm,
} from './QuoteSectionsEditor.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function QuoteDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const quoteQuery = useQuote(id);
  const quote = quoteQuery.data;

  // Inline images are stored as stable `psa-image:{id}` refs; resolve them to fresh
  // display URLs BEFORE mounting the editor (its state initializes from props).
  const imageIds = useMemo(() => {
    const set = new Set<string>();
    for (const section of quote?.sections ?? []) {
      if (section.html) {
        for (const imageId of collectNoteImageIds(section.html)) set.add(imageId);
      }
    }
    return [...set].sort();
  }, [quote]);
  const imagesQuery = useQuery({
    queryKey: ['quotes', 'images', quote?.id, imageIds],
    queryFn: () => resolveOpportunityImages(quote?.opportunityId ?? '', imageIds),
    enabled: Boolean(quote) && imageIds.length > 0,
  });
  const imageUrls = imagesQuery.data ?? {};

  const hydrated = useMemo<Quote | null>(() => {
    if (!quote) return null;
    return {
      ...quote,
      sections: (quote.sections ?? []).map((section) => ({
        ...section,
        html: section.html ? hydrateNoteImages(section.html, imageUrls) : section.html,
      })),
    };
  }, [quote, imageUrls]);

  const waitingForImages = imageIds.length > 0 && imagesQuery.isLoading;

  return (
    <div>
      <DataState
        isLoading={quoteQuery.isLoading || waitingForImages}
        isError={quoteQuery.isError}
        error={quoteQuery.error}
        onRetry={() => void quoteQuery.refetch()}
        isEmpty={false}
      >
        {hydrated && !waitingForImages ? <QuoteEditor key={hydrated.id} quote={hydrated} /> : null}
      </DataState>
    </div>
  );
}

/** Human label for a pushed quote's provenance ref ("isaac-loe-42" → Isaac LOE badge). */
function sourceRefLabel(sourceRef: string): string {
  return sourceRef.startsWith('isaac-loe-')
    ? 'Imported from Isaac LOE'
    : `Imported from ${sourceRef}`;
}

function QuoteEditor({ quote }: { quote: Quote }): JSX.Element {
  const navigate = useNavigate();
  const { can, isAdmin } = useAuth();
  const { inOverlay } = useDetailOverlay();
  const canEdit = can('sales', 'edit');
  const canDelete = can('sales', 'delete') || isAdmin;

  const [sections, setSections] = useState<SectionForm[]>(() => toSectionForms(quote.sections));
  const [lineForms, setLineForms] = useState<LineForm[]>(() => toLineForms(quote.lines));
  const [linesAfterSection, setLinesAfterSection] = useState(String(quote.linesAfterSection));
  const [lines2AfterSection, setLines2AfterSection] = useState(String(quote.lines2AfterSection));
  const [lines1Frequency, setLines1Frequency] = useState<QuoteLinesFrequency>(
    quote.lines1Frequency,
  );
  const [lines2Frequency, setLines2Frequency] = useState<QuoteLinesFrequency>(
    quote.lines2Frequency,
  );
  const [lines1BreakBefore, setLines1BreakBefore] = useState(quote.lines1PageBreakBefore);
  const [lines1BreakAfter, setLines1BreakAfter] = useState(quote.lines1PageBreakAfter);
  const [lines2BreakBefore, setLines2BreakBefore] = useState(quote.lines2PageBreakBefore);
  const [lines2BreakAfter, setLines2BreakAfter] = useState(quote.lines2PageBreakAfter);
  const [lines1Labels, setLines1Labels] = useState<LineLabelsForm>(() =>
    toLineLabelsForm(
      {
        sectionLabel: quote.lines1SectionLabel,
        qtyLabel: quote.lines1QtyLabel,
        unitPriceLabel: quote.lines1UnitPriceLabel,
        amountLabel: quote.lines1AmountLabel,
        totalLabel: quote.lines1TotalLabel,
        qtyHidden: quote.lines1QtyHidden,
        unitPriceHidden: quote.lines1UnitPriceHidden,
        amountHidden: quote.lines1AmountHidden,
        totalHidden: quote.lines1TotalHidden,
      },
      quote.lines1Frequency,
    ),
  );
  const [lines2Labels, setLines2Labels] = useState<LineLabelsForm>(() =>
    toLineLabelsForm(
      {
        sectionLabel: quote.lines2SectionLabel,
        qtyLabel: quote.lines2QtyLabel,
        unitPriceLabel: quote.lines2UnitPriceLabel,
        amountLabel: quote.lines2AmountLabel,
        totalLabel: quote.lines2TotalLabel,
        qtyHidden: quote.lines2QtyHidden,
        unitPriceHidden: quote.lines2UnitPriceHidden,
        amountHidden: quote.lines2AmountHidden,
        totalHidden: quote.lines2TotalHidden,
      },
      quote.lines2Frequency,
    ),
  );
  const [approvalPlacement, setApprovalPlacement] = useState<QuoteApprovalPlacement>(
    quote.approvalPlacement,
  );
  const [applyOpen, setApplyOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deliveryPreview, setDeliveryPreview] = useState<PublicQuoteView | null>(null);

  // Height of the sticky page header — keeps the Jump-to panel and jumped-to
  // sections clear of it while scrolling.
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  useEffect(() => {
    const el = stickyHeaderRef.current;
    if (!el) return;
    const update = (): void => setStickyHeaderHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const updateMutation = useUpdateQuote();
  const createMutation = useCreateQuote();
  const promoteMutation = usePromoteQuote();
  const deleteMutation = useDeleteQuote();
  const applyMutation = useApplyQuoteTemplate();
  const pdfMutation = usePreviewQuotePdf();
  const deliveryPreviewMutation = useQuoteDeliveryPreview();

  const versionsQuery = useQuotes({ page: 1, pageSize: 100, opportunityId: quote.opportunityId });
  const versions = (versionsQuery.data?.data ?? []).slice().sort((a, b) => b.version - a.version);

  const editable = quote.isLatest && quote.status !== 'approved' && canEdit;
  const lines = quote.lines ?? [];
  const invalidLines = editable && lineForms.some((line) => !line.description.trim());

  // Changing a group's frequency follows the default section/total labels along (only
  // when the user hasn't customized them).
  const changeLines1Frequency = (next: QuoteLinesFrequency): void => {
    setLines1Labels((prev) => {
      const oldDefaults = defaultLineLabels(lines1Frequency);
      const newDefaults = defaultLineLabels(next);
      return {
        ...prev,
        header: prev.header === oldDefaults.header ? newDefaults.header : prev.header,
        total: prev.total === oldDefaults.total ? newDefaults.total : prev.total,
      };
    });
    setLines1Frequency(next);
  };
  const changeLines2Frequency = (next: QuoteLinesFrequency): void => {
    setLines2Labels((prev) => {
      const oldDefaults = defaultLineLabels(lines2Frequency);
      const newDefaults = defaultLineLabels(next);
      return {
        ...prev,
        header: prev.header === oldDefaults.header ? newDefaults.header : prev.header,
        total: prev.total === oldDefaults.total ? newDefaults.total : prev.total,
      };
    });
    setLines2Frequency(next);
  };

  /** The editor's current (possibly unsaved) state, as the update payload shape. */
  const buildInput = (): UpdateQuoteInput => {
    const labels1 = toLineLabelInputs(lines1Labels, lines1Frequency);
    const labels2 = toLineLabelInputs(lines2Labels, lines2Frequency);
    return {
      linesAfterSection: Number(linesAfterSection),
      lines2AfterSection: Number(lines2AfterSection),
      lines1Frequency,
      lines2Frequency,
      lines1PageBreakBefore: lines1BreakBefore,
      lines1PageBreakAfter: lines1BreakAfter,
      lines2PageBreakBefore: lines2BreakBefore,
      lines2PageBreakAfter: lines2BreakAfter,
      lines1QtyLabel: labels1.qtyLabel,
      lines1UnitPriceLabel: labels1.unitPriceLabel,
      lines1AmountLabel: labels1.amountLabel,
      lines1TotalLabel: labels1.totalLabel,
      lines2QtyLabel: labels2.qtyLabel,
      lines2UnitPriceLabel: labels2.unitPriceLabel,
      lines2AmountLabel: labels2.amountLabel,
      lines2TotalLabel: labels2.totalLabel,
      lines1QtyHidden: labels1.qtyHidden,
      lines1UnitPriceHidden: labels1.unitPriceHidden,
      lines1AmountHidden: labels1.amountHidden,
      lines1TotalHidden: labels1.totalHidden,
      lines2QtyHidden: labels2.qtyHidden,
      lines2UnitPriceHidden: labels2.unitPriceHidden,
      lines2AmountHidden: labels2.amountHidden,
      lines2TotalHidden: labels2.totalHidden,
      lines1SectionLabel: labels1.sectionLabel,
      lines2SectionLabel: labels2.sectionLabel,
      approvalPlacement,
      // Store the stable `psa-image:{id}` refs instead of the volatile display URLs.
      sections: toSectionInputs(
        sections.map((s) => ({ ...s, html: serializeNoteImages(s.html) })),
      ),
      lines: toLineInputs(lineForms),
    };
  };

  const handleSave = (): void => {
    updateMutation.mutate({ id: quote.id, input: buildInput() });
  };

  const handleDeliveryPreview = (): void => {
    deliveryPreviewMutation.mutate(
      { id: quote.id, input: buildInput() },
      { onSuccess: (view) => setDeliveryPreview(view) },
    );
  };

  const handleApplied = (updated: Quote): void => {
    setSections(toSectionForms(updated.sections));
    setLineForms(toLineForms(updated.lines));
    setLinesAfterSection(String(updated.linesAfterSection));
    setLines2AfterSection(String(updated.lines2AfterSection));
    setLines1Frequency(updated.lines1Frequency);
    setLines2Frequency(updated.lines2Frequency);
    setLines1BreakBefore(updated.lines1PageBreakBefore);
    setLines1BreakAfter(updated.lines1PageBreakAfter);
    setLines2BreakBefore(updated.lines2PageBreakBefore);
    setLines2BreakAfter(updated.lines2PageBreakAfter);
    setLines1Labels(
      toLineLabelsForm(
        {
          sectionLabel: updated.lines1SectionLabel,
          qtyLabel: updated.lines1QtyLabel,
          unitPriceLabel: updated.lines1UnitPriceLabel,
          amountLabel: updated.lines1AmountLabel,
          totalLabel: updated.lines1TotalLabel,
          qtyHidden: updated.lines1QtyHidden,
          unitPriceHidden: updated.lines1UnitPriceHidden,
          amountHidden: updated.lines1AmountHidden,
          totalHidden: updated.lines1TotalHidden,
        },
        updated.lines1Frequency,
      ),
    );
    setLines2Labels(
      toLineLabelsForm(
        {
          sectionLabel: updated.lines2SectionLabel,
          qtyLabel: updated.lines2QtyLabel,
          unitPriceLabel: updated.lines2UnitPriceLabel,
          amountLabel: updated.lines2AmountLabel,
          totalLabel: updated.lines2TotalLabel,
          qtyHidden: updated.lines2QtyHidden,
          unitPriceHidden: updated.lines2UnitPriceHidden,
          amountHidden: updated.lines2AmountHidden,
          totalHidden: updated.lines2TotalHidden,
        },
        updated.lines2Frequency,
      ),
    );
    setApprovalPlacement(updated.approvalPlacement);
    setApplyOpen(false);
  };

  // Preview failures are surfaced here too — otherwise a rejected preview looks like the
  // button did nothing at all.
  const error =
    mutationError(updateMutation.error, 'Failed to save the quote.') ??
    mutationError(pdfMutation.error, 'Failed to render the PDF.') ??
    mutationError(deliveryPreviewMutation.error, 'Failed to render the delivery page preview.');

  const deleteError =
    deleteMutation.error instanceof ApiError
      ? deleteMutation.error.message
      : deleteMutation.isError
        ? 'Failed to delete the quote.'
        : null;

  // Sidebar quick navigation — each target element carries the matching id, in the
  // page's visual order (numbered sections, then lines, then the Legal Contract at the
  // bottom). Hidden sections are excluded (nothing meaningful to jump to).
  const legalSection = sections.find((s) => s.sectionIndex === QUOTE_LEGAL_SECTION_INDEX);
  const jumpEntries: { id: string; label: string }[] = [
    ...sections
      .filter((s) => !s.hidden && s.sectionIndex !== QUOTE_LEGAL_SECTION_INDEX)
      .map((s) => ({
        id: `quote-section-${s.sectionIndex}`,
        label: s.title.trim() || `Section ${s.sectionIndex}`,
      })),
    ...(editable
      ? [
          {
            id: 'quote-lines-section-1',
            label: `Lines 1 — ${
              lines1Labels.header.trim() || QUOTE_LINES_FREQUENCY_LABELS[lines1Frequency]
            }`,
          },
          {
            id: 'quote-lines-section-2',
            label: `Lines 2 — ${
              lines2Labels.header.trim() || QUOTE_LINES_FREQUENCY_LABELS[lines2Frequency]
            }`,
          },
          { id: 'quote-delivery-settings', label: 'Delivery page settings' },
        ]
      : // Read-only snapshots only render sections that have lines.
        [1, 2]
          .filter((group) =>
            lines.some((l) => (group === 2 ? l.lineGroup === 2 : l.lineGroup !== 2)),
          )
          .map((group) => ({
            id: `quote-lines-section-${group}`,
            label: `Lines ${group} — ${
              (group === 2 ? quote.lines2SectionLabel : quote.lines1SectionLabel) ??
              QUOTE_LINES_FREQUENCY_LABELS[
                group === 2 ? quote.lines2Frequency : quote.lines1Frequency
              ]
            }`,
          }))),
    {
      id: `quote-section-${QUOTE_LEGAL_SECTION_INDEX}`,
      label: legalSection?.title.trim() || QUOTE_LEGAL_SECTION_TITLE,
    },
  ];

  return (
    <div>
      {inOverlay ? null : (
        <Link
          to="/sales/quotes"
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← Back to quotes
        </Link>
      )}

      <div
        ref={stickyHeaderRef}
        className="sticky top-0 z-30 mb-4 border-b border-slate-200 bg-slate-50 pb-3 pt-1"
      >
      <PageHeader
        title={`Quote v${quote.version} — ${quote.opportunityName}`}
        description={`${quote.customerName}${quote.opportunityTypeName ? ` · ${quote.opportunityTypeName}` : ''}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
              History
            </Button>
            <Button
              variant="secondary"
              isLoading={pdfMutation.isPending}
              onClick={() => pdfMutation.mutate(quote.id)}
            >
              Download PDF
            </Button>
            <Button
              variant="secondary"
              isLoading={deliveryPreviewMutation.isPending}
              onClick={handleDeliveryPreview}
            >
              Preview delivery page
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                navigate(`/sales/opportunities/${quote.opportunityId}`)
              }
            >
              Go to opportunity
            </Button>
            {canEdit && quote.isLatest ? (
              <>
                <Button variant="secondary" onClick={() => setApplyOpen(true)}>
                  Apply template
                </Button>
                <Button variant="secondary" onClick={() => setSendOpen(true)}>
                  Send to customer
                </Button>
                <Button
                  variant="secondary"
                  isLoading={createMutation.isPending}
                  onClick={() =>
                    createMutation.mutate(quote.opportunityId, {
                      onSuccess: (created) => navigate(`/sales/quotes/${created.id}`),
                    })
                  }
                >
                  New version
                </Button>
              </>
            ) : null}
            {canEdit && !quote.isLatest ? (
              <Button
                isLoading={promoteMutation.isPending}
                onClick={() =>
                  promoteMutation.mutate(quote.id, {
                    onSuccess: (created) => navigate(`/sales/quotes/${created.id}`),
                  })
                }
              >
                Promote to latest
              </Button>
            ) : null}
            {editable ? (
              <Button
                isLoading={updateMutation.isPending}
                disabled={invalidLines}
                onClick={handleSave}
              >
                Save quote
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={QUOTE_STATUS_TONES[quote.status]}>{QUOTE_STATUS_LABELS[quote.status]}</Badge>
        <Badge tone={quote.isLatest ? 'blue' : 'gray'}>
          {quote.isLatest ? 'Latest version' : 'Superseded'}
        </Badge>
        {quote.sourceRef ? (
          quote.sourceUrl ? (
            <a
              href={quote.sourceUrl}
              target="_blank"
              rel="noreferrer"
              title={quote.sourceRef}
              className="hover:opacity-80"
            >
              <Badge tone="orange">{`${sourceRefLabel(quote.sourceRef)} ↗`}</Badge>
            </a>
          ) : (
            <span title={quote.sourceRef}>
              <Badge tone="orange">{sourceRefLabel(quote.sourceRef)}</Badge>
            </span>
          )
        ) : null}
        {quote.expirationDate ? (
          <span className="text-xs text-slate-500">
            Expires {formatDate(quote.expirationDate)} (opportunity close date)
          </span>
        ) : null}
        {quote.sentAt ? (
          <span className="text-xs text-slate-500">
            Sent {formatDateTime(quote.sentAt)}
            {quote.sentTo ? ` to ${quote.sentTo}` : ''}
          </span>
        ) : null}
        {quote.approvedAt ? (
          <span className="text-xs font-medium text-emerald-700">
            Approved by {quote.signerName ?? 'customer'}
            {quote.signerEmail ? ` (${quote.signerEmail})` : ''} on {formatDateTime(quote.approvedAt)}
          </span>
        ) : null}
        {quote.approvedAt &&
        quote.signerEmail &&
        !(quote.sentTo ?? quote.contactEmail ?? '')
          .toLowerCase()
          .includes(quote.signerEmail.toLowerCase()) ? (
          <span className="text-xs font-medium text-amber-700">
            Signer differs from recipient — sent to{' '}
            {quote.sentTo ?? quote.contactEmail ?? 'unknown'}, signed by{' '}
            {quote.signerName ?? 'customer'} ({quote.signerEmail})
          </span>
        ) : null}
        {!quote.isLatest ? (
          <span className="text-xs text-slate-500">
            Older versions are read-only — promote to edit this content as a new version.
          </span>
        ) : null}
      </div>
      </div>

      {error ? (
        <p className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      ) : null}
      {updateMutation.isSuccess ? (
        <p className="mb-4 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Quote saved.
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div>
          <QuoteSectionsEditor
            sections={sections}
            onChange={setSections}
            readOnly={!editable}
            slot="numbered"
            onUploadImage={
              editable ? (file) => uploadOpportunityImage(quote.opportunityId, file) : undefined
            }
          />

          {editable ? (
            <>
              {invalidLines ? (
                <p className="mt-6 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  Every line needs a description before saving.
                </p>
              ) : null}
              <QuoteLinesEditor
                lines={lineForms}
                onChange={setLineForms}
                groups={[
                  {
                    frequency: lines1Frequency,
                    placement: linesAfterSection,
                    pageBreakBefore: lines1BreakBefore,
                    pageBreakAfter: lines1BreakAfter,
                    labels: lines1Labels,
                    onFrequencyChange: changeLines1Frequency,
                    onPlacementChange: setLinesAfterSection,
                    onPageBreakBeforeChange: setLines1BreakBefore,
                    onPageBreakAfterChange: setLines1BreakAfter,
                    onLabelsChange: (patch) => setLines1Labels((prev) => ({ ...prev, ...patch })),
                  },
                  {
                    frequency: lines2Frequency,
                    placement: lines2AfterSection,
                    pageBreakBefore: lines2BreakBefore,
                    pageBreakAfter: lines2BreakAfter,
                    labels: lines2Labels,
                    onFrequencyChange: changeLines2Frequency,
                    onPlacementChange: setLines2AfterSection,
                    onPageBreakBeforeChange: setLines2BreakBefore,
                    onPageBreakAfterChange: setLines2BreakAfter,
                    onLabelsChange: (patch) => setLines2Labels((prev) => ({ ...prev, ...patch })),
                  },
                ]}
                approval={{
                  placement: approvalPlacement,
                  onChange: setApprovalPlacement,
                }}
              />
            </>
          ) : (
            /* Read-only line snapshot (superseded or approved versions). */
            <div className="mt-6 space-y-4">
              {lines.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-200 px-4 py-2">
                    <span className="text-sm font-medium text-slate-700">Product lines</span>
                  </div>
                  <p className="px-4 py-3 text-sm text-slate-500">No lines on this quote.</p>
                </div>
              ) : (
                [1, 2].map((group) => {
                  const groupLines = lines.filter((l) =>
                    group === 2 ? l.lineGroup === 2 : l.lineGroup !== 2,
                  );
                  if (groupLines.length === 0) return null;
                  const label =
                    QUOTE_LINES_FREQUENCY_LABELS[
                      group === 2 ? quote.lines2Frequency : quote.lines1Frequency
                    ];
                  return (
                    <div
                      key={group}
                      id={`quote-lines-section-${group}`}
                      className="scroll-mt-6 rounded-xl border border-slate-200 bg-white shadow-sm"
                    >
                      <div className="border-b border-slate-200 px-4 py-2">
                        <span className="text-sm font-medium text-slate-700">
                          {label} lines ({groupLines.length})
                        </span>
                      </div>
                      <table className="w-full text-left text-sm">
                        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-4 py-2 font-medium">Description</th>
                            <th className="px-4 py-2 font-medium">Qty</th>
                            <th className="px-4 py-2 font-medium">Unit price</th>
                            <th className="px-4 py-2 font-medium">Options</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {groupLines.map((line) =>
                            line.isComment ? (
                              <tr key={line.id}>
                                <td colSpan={4} className="px-4 py-2 text-slate-600">
                                  {line.description}
                                </td>
                              </tr>
                            ) : line.isBundleHeader ? (
                              <tr
                                key={line.id}
                                className={quote.approvedAt && !line.isSelected ? 'opacity-50' : ''}
                              >
                                <td className="px-4 py-2 font-semibold text-slate-800">
                                  {line.description}
                                  {quote.approvedAt && !line.isSelected ? (
                                    <span className="ml-2 text-xs font-normal text-slate-400">
                                      Not included
                                    </span>
                                  ) : null}
                                </td>
                                <td className="px-4 py-2" />
                                <td className="px-4 py-2" />
                                <td className="px-4 py-2">
                                  <span className="flex flex-wrap gap-1">
                                    <Badge tone="blue">{`Bundle${line.bundleKey ? ` ${line.bundleKey}` : ''}`}</Badge>
                                    {line.bundleLinesHidden ? (
                                      <Badge tone="gray">Lines hidden</Badge>
                                    ) : null}
                                  </span>
                                </td>
                              </tr>
                            ) : (
                            <tr
                              key={line.id}
                              className={quote.approvedAt && !line.isSelected ? 'opacity-50' : ''}
                            >
                              <td className="px-4 py-2 text-slate-800">
                                {line.description}
                                {quote.approvedAt && !line.isSelected ? (
                                  <span className="ml-2 text-xs text-slate-400">Not included</span>
                                ) : null}
                              </td>
                              <td className="px-4 py-2 text-slate-600">{Number(line.quantity)}</td>
                              <td className="px-4 py-2 text-slate-600">
                                {currency.format(Number(line.unitPrice))}
                              </td>
                              <td className="px-4 py-2">
                                <span className="flex flex-wrap gap-1">
                                  {line.isOptional ? <Badge tone="amber">Optional</Badge> : null}
                                  {line.bundleKey ? (
                                    <Badge tone="blue">{`Bundle ${line.bundleKey}${
                                      line.bundleMinChoose || line.bundleMaxChoose
                                        ? ` (choose ${line.bundleMinChoose ?? 1}${
                                            line.bundleMaxChoose
                                              ? `–${line.bundleMaxChoose}`
                                              : '+'
                                          })`
                                        : ''
                                    }`}</Badge>
                                  ) : null}
                                </span>
                              </td>
                            </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* The Legal Contract always edits at the very bottom, below the line sections. */}
          <div className="mt-6">
            <QuoteSectionsEditor
              sections={sections}
              onChange={setSections}
              readOnly={!editable}
              slot="legal"
              onUploadImage={
                editable ? (file) => uploadOpportunityImage(quote.opportunityId, file) : undefined
              }
            />
          </div>

          {canDelete && quote.status !== 'approved' ? (
            <div className="mt-6">
              <Button
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50"
                onClick={() => setConfirmDelete(true)}
              >
                Delete this version
              </Button>
              {deleteError ? (
                <p className="mt-2 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {deleteError}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Version history + delivery activity */}
        <div className="space-y-6">
          <div className="h-fit rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-4 py-2">
              <span className="text-sm font-medium text-slate-700">Versions</span>
            </div>
            <div className="divide-y divide-slate-100">
              {versions.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => navigate(`/sales/quotes/${v.id}`)}
                  className={`flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm hover:bg-slate-50 ${
                    v.id === quote.id ? 'bg-brand-50/60' : ''
                  }`}
                >
                  <span className="font-medium text-slate-800">v{v.version}</span>
                  <span className="flex items-center gap-2">
                    {v.isLatest ? <Badge tone="blue">Latest</Badge> : null}
                    <Badge tone={QUOTE_STATUS_TONES[v.status]}>
                      {QUOTE_STATUS_LABELS[v.status]}
                    </Badge>
                    <span className="text-xs text-slate-400">{formatDate(v.updatedAt)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <QuoteSowLinkPanel quote={quote} canManage={canEdit && quote.isLatest} />

          <QuoteVisitsPanel quote={quote} />

          <JumpToPanel entries={jumpEntries} topOffset={stickyHeaderHeight} />
        </div>
      </div>

      <SendQuoteEmailModal isOpen={sendOpen} quote={quote} onClose={() => setSendOpen(false)} />

      <Modal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={`History — Quote v${quote.version}`}
        size="lg"
        fillHeight
      >
        <HistoryPanel entityType="Quote" entityId={quote.id} enabled={historyOpen} />
      </Modal>

      <Modal
        isOpen={deliveryPreview !== null}
        onClose={() => setDeliveryPreview(null)}
        title="Delivery page preview"
        size="wide"
        fillHeight
      >
        {deliveryPreview ? (
          <div className="rounded-xl bg-slate-100 p-6">
            <QuoteDeliveryView view={deliveryPreview} previewMode />
          </div>
        ) : null}
      </Modal>

      <ApplyTemplateModal
        isOpen={applyOpen}
        quote={quote}
        onClose={() => setApplyOpen(false)}
        onApplied={handleApplied}
        applyMutation={applyMutation}
      />

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete quote version"
        message={`Delete v${quote.version}? ${
          quote.isLatest ? 'The previous version becomes the latest again.' : ''
        }`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={() =>
          deleteMutation.mutate(quote.id, {
            onSuccess: () => navigate('/sales/quotes'),
            // Close the dialog so the failure message on the page is visible.
            onError: () => setConfirmDelete(false),
          })
        }
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

/**
 * View-only public link for internal handoff (/sow/:token) — the delivery page without
 * approval or option toggles. Visits to it are not tracked.
 */
function QuoteSowLinkPanel({
  quote,
  canManage,
}: {
  quote: Quote;
  canManage: boolean;
}): JSX.Element {
  const regenMutation = useRegenerateQuoteSowLink();
  const disableMutation = useDisableQuoteSowLink();
  const [copied, setCopied] = useState(false);

  const url =
    quote.sowToken && quote.sowEnabled
      ? `${window.location.origin}/sow/${quote.sowToken}`
      : '';

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="h-fit rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
        <span className="text-sm font-medium text-slate-700">SOW for delivery team</span>
        <Badge tone={url ? 'green' : 'gray'}>{url ? 'Active' : 'Off'}</Badge>
      </div>
      <div className="space-y-2 px-4 py-3">
        <p className="text-xs text-slate-500">
          A view-only copy of this quote for the delivery team — no approval or option
          selection, and visits are not tracked.
        </p>
        {url ? (
          <>
            <div className="break-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-800">
              {url}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy link'}
              </Button>
              {canManage ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={regenMutation.isPending}
                    onClick={() => regenMutation.mutate(quote.id)}
                  >
                    Regenerate
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    isLoading={disableMutation.isPending}
                    onClick={() => disableMutation.mutate(quote.id)}
                  >
                    Disable
                  </Button>
                </>
              ) : null}
            </div>
          </>
        ) : canManage ? (
          <Button
            variant="secondary"
            size="sm"
            isLoading={regenMutation.isPending}
            onClick={() => regenMutation.mutate(quote.id)}
          >
            Generate link
          </Button>
        ) : (
          <p className="text-xs text-slate-400">No link has been generated.</p>
        )}
      </div>
    </div>
  );
}

/** Public-page visit tracking: total opens + the most recent visits. */
function QuoteVisitsPanel({ quote }: { quote: Quote }): JSX.Element {
  const visitsQuery = useQuoteVisits(quote.id, (quote.visitCount ?? 0) > 0);
  const visits = visitsQuery.data ?? [];

  return (
    <div className="h-fit rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
        <span className="text-sm font-medium text-slate-700">Customer visits</span>
        <Badge tone={(quote.visitCount ?? 0) > 0 ? 'green' : 'gray'}>
          {String(quote.visitCount ?? 0)}
        </Badge>
      </div>
      {(quote.visitCount ?? 0) === 0 ? (
        <p className="px-4 py-3 text-xs text-slate-500">
          {quote.deliveryEnabled
            ? 'No opens yet — visits appear here when the customer opens the quote link.'
            : 'The quote has not been shared yet. Use "Send to customer" to deliver it.'}
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {quote.lastVisitAt ? (
            <p className="px-4 py-2 text-xs text-slate-500">
              Last opened {formatDateTime(quote.lastVisitAt)}
            </p>
          ) : null}
          {visits.slice(0, 10).map((visit) => (
            <div key={visit.id} className="px-4 py-2 text-xs text-slate-600">
              <span className="font-medium text-slate-700">{formatDateTime(visit.visitedAt)}</span>
              {visit.ipAddress ? <span className="ml-2 text-slate-400">{visit.ipAddress}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Sidebar quick-nav: scrolls to a text section, lines section, or the delivery card. */
function JumpToPanel({
  entries,
  topOffset,
}: {
  entries: { id: string; label: string }[];
  topOffset: number;
}): JSX.Element {
  return (
    <div
      className="sticky h-fit rounded-xl border border-slate-200 bg-white shadow-sm"
      style={{ top: topOffset + 16 }}
    >
      <div className="border-b border-slate-200 px-4 py-2">
        <span className="text-sm font-medium text-slate-700">Jump to</span>
      </div>
      <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
        {entries.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => {
              const target = document.getElementById(entry.id);
              if (!target) return;
              // Keep the jumped-to section clear of the sticky page header.
              target.style.scrollMarginTop = `${topOffset + 16}px`;
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="block w-full truncate px-4 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            {entry.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ApplyTemplateModal({
  isOpen,
  quote,
  onClose,
  onApplied,
  applyMutation,
}: {
  isOpen: boolean;
  quote: Quote;
  onClose: () => void;
  onApplied: (updated: Quote) => void;
  applyMutation: ReturnType<typeof useApplyQuoteTemplate>;
}): JSX.Element {
  const templatesQuery = useQuoteTemplates(isOpen);
  const [templateId, setTemplateId] = useState('');
  // Which template parts to import — everything is checked by default.
  const [checkedSections, setCheckedSections] = useState<Set<number>>(new Set());
  const [includeLines, setIncludeLines] = useState(true);
  const [includeLegal, setIncludeLegal] = useState(true);

  const templates = (templatesQuery.data ?? []).filter((t: QuoteTemplate) => t.isActive);
  // Templates for the opp's type first, then untyped ones; other types are excluded
  // (the quote's type matches the opportunity's type).
  const matching = templates.filter(
    (t) => !t.opportunityTypeId || t.opportunityTypeId === quote.opportunityTypeId,
  );

  // The chosen template's detail (sections + lines) drives the part checkboxes.
  const templateQuery = useQuoteTemplate(templateId);
  const template = templateId ? templateQuery.data : undefined;
  const templateSections = template?.sections ?? [];
  // Only the template's NON-HIDDEN numbered sections are offered for import.
  const numberedSections = templateSections.filter(
    (s) => s.sectionIndex !== QUOTE_LEGAL_SECTION_INDEX && !s.hidden,
  );
  const legalSection = templateSections.find(
    (s) => s.sectionIndex === QUOTE_LEGAL_SECTION_INDEX,
  );
  const lineCount = (template?.lines ?? []).length;

  // Check everything whenever the chosen template (re)loads.
  useEffect(() => {
    setCheckedSections(new Set(numberedSections.map((s) => s.sectionIndex)));
    setIncludeLines(true);
    setIncludeLegal(true);
    // Reset when the loaded template changes, not on every render.
  }, [template?.id, templateSections.length]);

  const toggleSection = (index: number, checked: boolean): void => {
    setCheckedSections((prev) => {
      const next = new Set(prev);
      if (checked) next.add(index);
      else next.delete(index);
      return next;
    });
  };

  const nothingSelected = checkedSections.size === 0 && !includeLines && !includeLegal;

  const error =
    applyMutation.error instanceof ApiError
      ? applyMutation.error.message
      : applyMutation.isError
        ? 'Failed to apply the template.'
        : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Apply quote template"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={applyMutation.isPending}>
            Cancel
          </Button>
          <Button
            isLoading={applyMutation.isPending}
            disabled={!templateId || !template || nothingSelected}
            onClick={() =>
              applyMutation.mutate(
                {
                  id: quote.id,
                  templateId,
                  input: {
                    sectionIndexes: [...checkedSections].sort((a, b) => a - b),
                    includeLines,
                    includeLegal,
                  },
                },
                { onSuccess: (updated) => onApplied(updated) },
              )
            }
          >
            Apply template
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SelectField
          label="Template"
          value={templateId}
          options={[
            { value: '', label: templatesQuery.isLoading ? 'Loading…' : 'Choose a template…' },
            ...matching.map((t) => ({
              value: t.id,
              label: `${t.name}${t.opportunityTypeName ? ` (${t.opportunityTypeName})` : ''}`,
            })),
          ]}
          onChange={(e) => setTemplateId(e.target.value)}
        />
        {!templatesQuery.isLoading && matching.length === 0 ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
            No active templates match this opportunity&apos;s type. Create one under
            Administration → Quote Templates.
          </p>
        ) : null}

        {templateId && templateQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading template contents…</p>
        ) : null}

        {template ? (
          <>
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Importing <strong>OVERWRITES</strong> the checked parts of this quote with the
              template&apos;s content. Anything left unchecked stays as it is on the quote.
              Imported lines are also appended to the opportunity&apos;s products.
            </p>
            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sections to import
              </legend>
              <div className="grid gap-1 sm:grid-cols-2">
                {numberedSections.map((s) => (
                  <CheckboxField
                    key={s.sectionIndex}
                    label={s.title?.trim() || `Section ${s.sectionIndex}`}
                    checked={checkedSections.has(s.sectionIndex)}
                    onChange={(e) => toggleSection(s.sectionIndex, e.target.checked)}
                  />
                ))}
                <CheckboxField
                  label={`Lines${lineCount > 0 ? ` (${lineCount})` : ''}`}
                  checked={includeLines}
                  onChange={(e) => setIncludeLines(e.target.checked)}
                />
                <CheckboxField
                  label={legalSection?.title?.trim() || QUOTE_LEGAL_SECTION_TITLE}
                  checked={includeLegal}
                  onChange={(e) => setIncludeLegal(e.target.checked)}
                />
              </div>
              {numberedSections.length === 0 ? (
                <p className="mt-2 text-xs text-slate-500">
                  This template has no visible numbered sections.
                </p>
              ) : null}
              {nothingSelected ? (
                <p className="mt-2 text-xs text-rose-600">
                  Check at least one part to import.
                </p>
              ) : null}
            </fieldset>
          </>
        ) : null}

        {error ? (
          <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
        ) : null}
      </div>
    </Modal>
  );
}
